import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Eye, EyeOff, Plus, ShieldCheck } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/Card';
import Button from '../../ui/Button';
import Input from '../../ui/Input';
import { PhoneInput } from '../../ui/PhoneInput';
import { LoadingSpinner } from '../../ui/Loading';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/Table';
import { FormField } from '../../ui/CommonForm';
import { useCreateSuperAgent, useSuperAgents } from '../../../hooks/useApi';
import { usePermission } from '../../../hooks/useAuth';
import { PageHeader } from '@kioskgaming/ui';
import {
  DISPLAY_NAME_MAX_LENGTH,
  getDisplayNameValidationError,
  validateDisplayName,
} from '../../../utils/displayName';
import { truncateText } from '../../../utils';
import {
  CDN_AGENT_USERNAME_MAX_LENGTH,
  getCdnAgentUsernameError,
  normalizeCdnAgentUsername,
} from '../../../utils/username';

const PASSWORD_MIN = 8;
const TABLE_NAME_DISPLAY_MAX = 40;

const percentInputToRate = (value: string): number => {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.min(1, Number((n / 100).toFixed(4)));
};

const rateToPercent = (value: unknown): string => {
  const n = Number(value ?? 0);
  if (!Number.isFinite(n)) return '0.00';
  return (n * 100).toFixed(2);
};

const SuperAgentManagement: React.FC = () => {
  const canManage = usePermission('agentManagement');
  const { data, isLoading, error, isFetching } = useSuperAgents(canManage);
  const createSuperAgent = useCreateSuperAgent();
  const [createOpen, setCreateOpen] = useState(false);
  const [emailSearch, setEmailSearch] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({
    name: '',
    username: '',
    email: '',
    phone: '',
    password: '',
    cost_percent: '0',
    can_delegate_popups: false,
  });

  const rows = useMemo(() => data?.data ?? [], [data]);
  const filteredRows = useMemo(() => {
    const normalizedEmailSearch = emailSearch.trim().toLowerCase();
    if (!normalizedEmailSearch) return rows;
    return rows.filter((item) => item.email.toLowerCase().includes(normalizedEmailSearch));
  }, [rows, emailSearch]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canManage) return;

    const nameError = getDisplayNameValidationError(form.name);
    if (nameError) {
      toast.error(nameError);
      return;
    }
    const name = validateDisplayName(form.name);
    const usernameError = getCdnAgentUsernameError(form.username);
    if (usernameError) {
      toast.error(usernameError);
      return;
    }
    const username = normalizeCdnAgentUsername(form.username).trim();
    const email = form.email.trim();
    const phone = form.phone.trim();
    if (!email && !phone) {
      toast.error('Email or phone is required. You only need to provide one.');
      return;
    }
    if (form.password.length < PASSWORD_MIN) {
      toast.error(`Password must be at least ${PASSWORD_MIN} characters.`);
      return;
    }

    await createSuperAgent.mutateAsync({
      name,
      username,
      email: email || undefined,
      phone: phone || undefined,
      password: form.password,
      cost_rate: percentInputToRate(form.cost_percent),
      can_delegate_popups: form.can_delegate_popups,
    });
    setForm({ name: '', username: '', email: '', phone: '', password: '', cost_percent: '0', can_delegate_popups: false });
    setCreateOpen(false);
    setShowPassword(false);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Super Agent Management"
        description="Manage super agents and delegation hierarchy"
        actions={
          canManage ? (
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create super agent
            </Button>
          ) : null
        }
      />

      {canManage && createOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/50 p-4 flex items-center justify-center"
          role="dialog"
          aria-modal="true"
          aria-labelledby="new-super-agent-modal-title"
          onClick={(e) => {
            if (e.target === e.currentTarget && !createSuperAgent.isLoading) setCreateOpen(false);
          }}
        >
          <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <CardHeader>
              <CardTitle id="new-super-agent-modal-title" className="text-lg flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-primary" />
                New super agent
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCreate} className="space-y-4">
                <FormField label="Name" htmlFor="create-super-agent-name" required>
                    <Input
                      id="create-super-agent-name"
                      required
                      placeholder="Display name"
                      value={form.name}
                      maxLength={DISPLAY_NAME_MAX_LENGTH}
                      onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                      disabled={createSuperAgent.isLoading}
                    />
                    <p className="mt-1 text-xs text-muted-foreground">
                      Letters, numbers, and spaces only. Max {DISPLAY_NAME_MAX_LENGTH} characters.
                    </p>
                  </FormField>
                  <FormField label="Username" htmlFor="create-super-agent-username" required>
                    <Input
                      id="create-super-agent-username"
                      required
                      autoComplete="username"
                      placeholder="Login username"
                      maxLength={CDN_AGENT_USERNAME_MAX_LENGTH}
                      value={form.username}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          username: normalizeCdnAgentUsername(e.target.value).slice(0, CDN_AGENT_USERNAME_MAX_LENGTH),
                        }))
                      }
                      disabled={createSuperAgent.isLoading}
                    />
                    <p className="mt-1 text-xs text-muted-foreground">
                      Lowercase letters, numbers, and <span className="font-medium">_</span>; must start with a letter.
                    </p>
                  </FormField>
                  <FormField label="Email" htmlFor="create-super-agent-email" required>
                    <Input
                      id="create-super-agent-email"
                      type="email"
                      autoComplete="email"
                      placeholder="Email address"
                      value={form.email}
                      onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                      disabled={createSuperAgent.isLoading}
                    />
                  </FormField>
                <FormField label="Phone" htmlFor="create-super-agent-phone">
                  <PhoneInput
                    id="create-super-agent-phone"
                    placeholder="Phone number"
                    value={form.phone}
                    onChange={(phone) => setForm((f) => ({ ...f, phone }))}
                    disabled={createSuperAgent.isLoading}
                    defaultCountry="US"
                  />
                  <p className="mt-1 text-xs text-muted-foreground">
                    Email or phone is required — you only need to fill in one of them.
                  </p>
                </FormField>
                <FormField label="Password" htmlFor="create-super-agent-password" required>
                    <div className="relative">
                      <Input
                        id="create-super-agent-password"
                        required
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="new-password"
                        placeholder="Password"
                        value={form.password}
                        onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                        className="pr-10"
                        disabled={createSuperAgent.isLoading}
                      />
                      <button
                        type="button"
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        onClick={() => setShowPassword((v) => !v)}
                        disabled={createSuperAgent.isLoading}
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">At least {PASSWORD_MIN} characters.</p>
                  </FormField>
                <FormField label="Cost rate (%)" htmlFor="super-agent-cost-rate">
                    <div className="relative max-w-xs">
                      <Input
                        id="super-agent-cost-rate"
                        type="number"
                        step="0.01"
                        min={0}
                        max={100}
                        placeholder="0.00"
                        value={form.cost_percent}
                        className="pr-8"
                        onChange={(e) => setForm((f) => ({ ...f, cost_percent: e.target.value }))}
                        onBlur={(e) => {
                          const raw = e.target.value.trim();
                          if (!raw) {
                            setForm((f) => ({ ...f, cost_percent: '0' }));
                            return;
                          }
                          const n = Number(raw);
                          if (!Number.isFinite(n)) {
                            setForm((f) => ({ ...f, cost_percent: '0' }));
                            return;
                          }
                          const clamped = Math.min(100, Math.max(0, n));
                          setForm((f) => ({ ...f, cost_percent: String(clamped) }));
                        }}
                        disabled={createSuperAgent.isLoading}
                      />
                      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                        %
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Set from 0 to 100. This is the super agent base cost rate and child agent/shop rates must be at
                      least this value.
                    </p>
                </FormField>
                <label className="flex items-start gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={form.can_delegate_popups}
                    onChange={(e) => setForm((f) => ({ ...f, can_delegate_popups: e.target.checked }))}
                    disabled={createSuperAgent.isLoading}
                  />
                  <span>
                    Allow this super agent to send popups and broadcasts down the hierarchy (agents, shops, players)
                    and to grant popup permissions when creating managed agents.
                  </span>
                </label>
                <div className="flex items-center gap-2">
                  <Button type="button" variant="outline" onClick={() => setCreateOpen(false)} disabled={createSuperAgent.isLoading}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={createSuperAgent.isLoading || form.password.length < PASSWORD_MIN}
                  >
                    {createSuperAgent.isLoading ? 'Creating…' : 'Create'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      <Card>
        <CardContent className="p-0">
          <div className="px-4 py-3 border-b bg-muted/20">
            <div className="max-w-md space-y-1">
              <label htmlFor="super-agent-email-search" className="text-sm font-medium">
                Search email
              </label>
              <Input
                id="super-agent-email-search"
                type="search"
                value={emailSearch}
                onChange={(e) => setEmailSearch(e.target.value)}
                placeholder="Enter super agent email"
              />
            </div>
          </div>
          {isLoading ? (
            <div className="flex items-center justify-center h-48">
              <LoadingSpinner />
            </div>
          ) : error ? (
            <div className="p-6 text-center text-red-600">Failed to load super agents.</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Cost (%)</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right w-[120px]">Manage</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-10 text-muted-foreground">
                        {rows.length === 0 ? 'No super agents yet' : 'No super agents found'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredRows.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell className="max-w-[16rem]">
                          <Link
                            to={`/super-agents/${item.id}`}
                            title={item.name}
                            className="font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-sm"
                          >
                            {truncateText(item.name, TABLE_NAME_DISPLAY_MAX)}
                          </Link>
                        </TableCell>
                        <TableCell>{item.email}</TableCell>
                        <TableCell>{item.phone || '—'}</TableCell>
                        <TableCell>{rateToPercent(item.costRate)}</TableCell>
                        <TableCell>{item.status}</TableCell>
                        <TableCell className="text-right">
                          <Link
                            to={`/super-agents/${item.id}`}
                            className="inline-flex h-9 items-center justify-center rounded-md border border-input px-3 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                          >
                            View
                          </Link>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
        <div className="px-6 py-3 text-sm text-muted-foreground border-t">
          Total: <span className="font-medium text-foreground">{filteredRows.length}</span>
          {isFetching && !isLoading && <span className="ml-2">(Refreshing…)</span>}
        </div>
      </Card>
    </div>
  );
};

export default SuperAgentManagement;
